import React from 'react';
import { FiChevronRight, FiCornerUpLeft } from 'react-icons/fi';
import type { InquiryItem } from '../types';

interface InquiryCardProps {
  inquiry: InquiryItem;
  isSelected: boolean;
  onSelect: () => void;
  onReply?: (inquiry: InquiryItem) => void;
}

const statusBadgeStyles: Record<string, string> = {
  new: 'bg-[#1B2B4B] text-[#4A90FF]',
  replied: 'bg-[#1A3A28] text-[#3DD06C]',
  offer_sent: 'bg-[#3A2E1A] text-[#F5A623]',
  closed: 'bg-[#2A2A2E] text-[#8E8E93]',
};

const statusLabels: Record<string, string> = {
  new: 'New',
  replied: 'Replied',
  offer_sent: 'Offer Sent',
  closed: 'Closed',
};

const avatarColors: Record<string, string> = {
  D: 'bg-[#6366F1]',
  S: 'bg-[#0D9488]',
  M: 'bg-[#EA580C]',
  R: 'bg-[#DC2626]',
};

export const InquiryCard: React.FC<InquiryCardProps> = ({
  inquiry,
  isSelected,
  onSelect,
  onReply,
}) => {
  const initial = inquiry.guestName.charAt(0).toUpperCase() || 'G';
  const bgColor = inquiry.avatarColor || avatarColors[initial] || 'bg-indigo-600';

  return (
    <article
      onClick={onSelect}
      className={`cursor-pointer rounded-2xl border bg-[#1C1C1E] p-4 transition-all duration-200 ${
        isSelected
          ? 'border-[#1784FF] shadow-[0_0_15px_rgba(23,132,255,0.12)] ring-1 ring-[#1784FF]'
          : 'border-[#2C2C2E] hover:border-[#3A3A3C] hover:bg-[#222225]'
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-bold text-white ${bgColor}`}
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-tight text-white truncate">
              {inquiry.guestName}
            </h3>
            <p className="text-xs text-zinc-400 truncate">{inquiry.guestEmail}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              statusBadgeStyles[inquiry.status] || 'bg-zinc-800 text-zinc-300'
            }`}
          >
            {statusLabels[inquiry.status] || inquiry.status}
          </span>
          <FiChevronRight size={18} className="text-zinc-500" />
        </div>
      </div>

      {inquiry.interestedIn && (
        <div className="my-2 inline-block">
          <span className="rounded-lg border border-[#3A3A3C] bg-[#2C2C2E] px-3 py-1.5 text-xs font-medium text-zinc-200">
            Interested in: <span className="font-semibold text-white">{inquiry.interestedIn}</span>
          </span>
        </div>
      )}

      <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-zinc-400">
        {inquiry.message && inquiry.message !== 'No preview message available'
          ? inquiry.message
          : "Hi, We're interested in booking your service and would like to know your availability..."}
      </p>

      <div className="flex items-center justify-between gap-2 border-t border-[#2C2C2E]/60 pt-2.5 text-xs text-[#8E8E93]">
        <span className="truncate">
          {inquiry.timestamp}
          {inquiry.source && ` • Source: ${inquiry.source}`}
        </span>

        <div className="flex items-center gap-2 shrink-0">
          {onReply && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReply(inquiry);
              }}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white transition cursor-pointer"
            >
              <FiCornerUpLeft size={12} />
              <span>Reply</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
};
